import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Badge } from '@/shared/components/ui/badge';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Button } from '@/shared/components/ui/button';
import { ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { formatDate } from '@/infrastructure/lib/date';
import { useAuth } from '@/features/Auth/state/AuthContext';
import { getStorageItemChunks, StorageChunk } from '../services/storageService';
import { KnowledgeStorage as KnowledgeStorageType } from '../types/knowledge-storage';
import { documentOrigin } from '../utils/documentOrigin';

const CHUNKS_PAGE_SIZE = 20;

interface EntryDetailsProps {
  entry: KnowledgeStorageType;
}

/**
 * Resumo de schema de um documento tabular (PBI #81666, Fase 5.2), gravado em
 * `storage.metadata.tabular` pela ingestão — render dedicado nas abas Metadados
 * e Chunks (sem despejar JSON cru).
 */
interface TabularSchemaSummary {
  sheet_count?: number;
  total_rows?: number;
  sheets?: Array<{
    sheet_name?: string;
    row_count?: number;
    /** Representação: 'markdown' (aba pequena), 'data_card' (grande) ou 'descriptive' (aba degradada). */
    representation?: string;
    columns?: Array<{ name?: string; type?: string; samples?: unknown[] }>;
  }>;
}

// Corrige mojibake: bytes UTF-8 lidos como Latin-1. fatal:true garante que strings já corretas disparam catch e retornam intactas.
function decodeFileName(name: string | null | undefined): string | null | undefined {
  if (!name) return name;
  if ([...name].some((c) => c.charCodeAt(0) > 0xFF)) return name;
  try {
    const bytes = Uint8Array.from(name, (c) => c.charCodeAt(0));
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return name;
  }
}

function formatBytes(bytes: number | null | undefined): string {
  if (!bytes && bytes !== 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function scoreTone(score: number): string {
  if (score >= 7) return 'bg-emerald-500';
  if (score >= 4) return 'bg-amber-500';
  return 'bg-rose-500';
}

// Altura fixa do painel das tabs — garante que o dialog não pula ao trocar de aba.
const TAB_BODY_HEIGHT = 'h-[440px]';

export function EntryDetails({ entry }: EntryDetailsProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isSuperAdmin = !!user?.isSuperAdmin;

  // Quem enviou e por qual meio (AB#87121, AC11).
  const origin = documentOrigin(entry.metadata);
  const originLabel = origin?.channel
    ? [origin.author, t(`knowledge.storage.origin.channels.${origin.channel}`), origin.client]
        .filter(Boolean)
        .join(' · ')
    : null;

  const [chunks, setChunks] = useState<StorageChunk[]>([]);
  const [chunksTotal, setChunksTotal] = useState<number>(0);
  const [chunksLoading, setChunksLoading] = useState<boolean>(false);
  const [chunksError, setChunksError] = useState<string | null>(null);
  const [chunksPage, setChunksPage] = useState<number>(1);
  const [activeTab, setActiveTab] = useState<'details' | 'metadata' | 'quality' | 'chunks'>('details');

  useEffect(() => {
    if (activeTab !== 'chunks') return;
    if (!isSuperAdmin) return;
    if (!entry.knowledge_base_id || !entry.id) return;

    let cancelled = false;
    setChunksLoading(true);
    setChunksError(null);
    getStorageItemChunks(entry.knowledge_base_id, entry.id, chunksPage, CHUNKS_PAGE_SIZE)
      .then((res) => {
        if (cancelled) return;
        setChunks(res.data || []);
        setChunksTotal(res.total ?? 0);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setChunksError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (cancelled) return;
        setChunksLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab, isSuperAdmin, entry.id, entry.knowledge_base_id, chunksPage]);

  // Ordena a página atual por chunk_index para garantir leitura na ordem do
  // documento mesmo que a ingestão tenha gravado fora de ordem. Cheap: pageSize.
  const sortedChunks = useMemo(() => {
    return [...chunks].sort((a, b) => {
      const ai = Number((a.metadata?.chunk_index as number | undefined) ?? Number.POSITIVE_INFINITY);
      const bi = Number((b.metadata?.chunk_index as number | undefined) ?? Number.POSITIVE_INFINITY);
      return ai - bi;
    });
  }, [chunks]);

  const chunksTotalPages = Math.max(1, Math.ceil(chunksTotal / CHUNKS_PAGE_SIZE));
  const chunksRangeStart = chunksTotal === 0 ? 0 : (chunksPage - 1) * CHUNKS_PAGE_SIZE + 1;

  const metadata = entry.metadata || {};

  const rawFacets = Array.isArray((metadata as { facets?: unknown }).facets)
    ? ((metadata as { facets: unknown[] }).facets as unknown[])
    : [];
  const facetEntries: Array<{ name: string; value: string }> = rawFacets
    .filter((f): f is { name: unknown; value: unknown } =>
      !!f && typeof f === 'object' && 'name' in (f as object) && 'value' in (f as object),
    )
    .map((f) => ({ name: String(f.name ?? ''), value: String(f.value ?? '') }))
    .filter((f) => f.name && f.value);
  const metadataWithoutFacets: Record<string, unknown> = { ...metadata };
  delete (metadataWithoutFacets as { facets?: unknown }).facets;
  // PBI #81666 — o resumo de schema (`tabular`) tem render dedicado; não
  // despejamos como JSON cru na lista de metadados.
  delete (metadataWithoutFacets as { tabular?: unknown }).tabular;
  const metaEntries = Object.entries(metadataWithoutFacets);

  const config = entry.config || {};
  const qualityScore = typeof config.quality_score === 'number' ? config.quality_score : undefined;
  const completenessScore = typeof config.completeness_score === 'number' ? config.completeness_score : undefined;
  const relevanceScore = typeof config.relevance_score === 'number' ? config.relevance_score : undefined;
  const totalChunks = typeof config.total_chunks === 'number' ? config.total_chunks : undefined;
  const processedChunks = typeof config.processed_chunks === 'number' ? config.processed_chunks : undefined;

  // PBI 79328 — atributos doc-level do processamento foram agregados em
  // `storage.metadata.processing` pelo EventOrchestration. Exibimos eles aqui
  // como info técnica do índice (estratégia de chunking, tipo de documento).
  const processing = (metadata as { processing?: Record<string, unknown> }).processing || {};
  const chunkMethod = typeof processing.chunk_method === 'string' ? processing.chunk_method : undefined;
  const documentType = typeof processing.document_type === 'string' ? processing.document_type : undefined;
  const processingModel = typeof processing.model === 'string' ? processing.model : undefined;
  const processingProvider = typeof processing.provider === 'string' ? processing.provider : undefined;
  const processedAt = typeof processing.processed_at === 'string' ? processing.processed_at : undefined;

  // PBI #81666 (Fase 5.2) — documento tabular: detecção + resumo de schema
  // (storage.metadata.tabular). Habilita render dedicado de esquema/abas.
  const isTabular = config.kind === 'tabular' || /\.(xlsx|xls|csv|tsv|ods)$/i.test(entry.file_name || '');
  const tabularSummary = (metadata as { tabular?: TabularSchemaSummary }).tabular;
  const tabularSheets = isTabular && Array.isArray(tabularSummary?.sheets) ? tabularSummary!.sheets! : [];
  // Aba degradada (PBI #81711): indexada em modo descritivo ('descriptive') por ter
  // formatação complexa — pesquisável por significado, mas fora do caminho SQL.
  const hasDegradedSheets = tabularSheets.some(s => s.representation === 'descriptive');

  const tabCount = isSuperAdmin ? 4 : 3;

  return (
    <div className="flex flex-col h-full space-y-4">
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)} className="w-full">
        <TabsList className="grid w-full h-9" style={{ gridTemplateColumns: `repeat(${tabCount}, minmax(0, 1fr))` }}>
          <TabsTrigger value="details" className="text-xs">{t('knowledge.storage.details.tabs.details')}</TabsTrigger>
          <TabsTrigger value="metadata" className="text-xs">{t('knowledge.storage.details.tabs.metadata')}</TabsTrigger>
          <TabsTrigger value="quality" className="text-xs">{t('knowledge.storage.details.tabs.quality')}</TabsTrigger>
          {isSuperAdmin && (
            <TabsTrigger value="chunks" className="text-xs">{t('knowledge.storage.details.tabs.chunks')}</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="details" className={`${TAB_BODY_HEIGHT} mt-4`}>
          <ScrollArea className="h-full pr-3">
            <div className="space-y-5">
              {entry.description && (
                <div className="space-y-1.5">
                  <SectionLabel>{t('knowledge.imports.fields.description')}</SectionLabel>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">
                    {entry.description}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                <Field label={t('knowledge.imports.fields.type')} value={entry.type} />
                <Field label={t('knowledge.imports.fields.created_at')} value={formatDate(entry.created_at)} />
                <Field label={t('knowledge.storage.details.fields.fileName')} value={decodeFileName(entry.file_name)} />
                <Field label={t('knowledge.storage.details.fields.fileSize')} value={formatBytes(entry.file_size)} />
                <Field label={t('knowledge.storage.details.fields.fileType')} value={entry.file_type} />
                <Field label={t('knowledge.imports.fields.status')} value={entry.status} />
                <Field label={t('knowledge.storage.origin.column')} value={originLabel} />
              </div>
              {origin?.textOnly && (
                <p className="text-xs text-muted-foreground mt-4">{t('knowledge.storage.origin.textOnlyHint')}</p>
              )}

            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="metadata" className={`${TAB_BODY_HEIGHT} mt-4`}>
          {metaEntries.length === 0 && facetEntries.length === 0 && tabularSheets.length === 0 ? (
            <EmptyState>{t('knowledge.storage.details.noMetadata')}</EmptyState>
          ) : (
            <ScrollArea className="h-full pr-3">
              <div className="space-y-4">
                {tabularSheets.length > 0 && (
                  <div className="space-y-2">
                    <SectionLabel>{t('knowledge.storage.details.tabular.schema')}</SectionLabel>
                    {tabularSheets.map((sheet, si) => (
                      <div key={si} className="rounded-md border bg-muted/20 px-3 py-2.5 space-y-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-sm font-medium truncate">{sheet.sheet_name || `#${si + 1}`}</span>
                            {sheet.representation && (
                              <Badge variant="outline" className="font-normal text-[10px] py-0 shrink-0">
                                {sheet.representation}
                              </Badge>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                            {t('knowledge.storage.details.tabular.rowCount', { count: sheet.row_count ?? 0 })}
                            {' · '}
                            {t('knowledge.storage.details.tabular.columnCount', { count: sheet.columns?.length ?? 0 })}
                          </span>
                        </div>
                        {Array.isArray(sheet.columns) && sheet.columns.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {sheet.columns.map((col, ci) => (
                              <Badge key={ci} variant="secondary" className="font-normal text-[11px] gap-1 py-0.5">
                                <span>{col.name}</span>
                                {col.type && <span className="text-muted-foreground/60">{col.type}</span>}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {facetEntries.length > 0 && (
                  <div className="rounded-md border bg-muted/20 px-3 py-2.5">
                    <div className="grid grid-cols-[180px_1fr] gap-3">
                      <div className="font-mono text-xs text-muted-foreground pt-0.5">
                        {t('knowledge.storage.details.fields.facets').toLowerCase()}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {facetEntries.map((f, idx) => (
                          <Badge
                            key={`${f.name}-${f.value}-${idx}`}
                            variant="secondary"
                            className="font-normal text-[11px] gap-1 py-0.5"
                          >
                            <span className="text-muted-foreground">{f.name}</span>
                            <span className="text-muted-foreground/60">·</span>
                            <span>{f.value}</span>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {metaEntries.length > 0 && (
                  <div className="divide-y rounded-md border bg-muted/20">
                    {metaEntries.map(([key, value]) => (
                      <MetadataRow key={key} k={key} value={value} />
                    ))}
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
        </TabsContent>

        <TabsContent value="quality" className={`${TAB_BODY_HEIGHT} mt-4`}>
          <ScrollArea className="h-full pr-3">
            <div className="space-y-5">
              {qualityScore === undefined && completenessScore === undefined && relevanceScore === undefined ? (
                <EmptyState>{t('knowledge.storage.details.quality.empty')}</EmptyState>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <ScoreCard label={t('knowledge.storage.details.quality.overall')} score={qualityScore} t={t} />
                  <ScoreCard label={t('knowledge.storage.details.quality.completeness')} score={completenessScore} t={t} />
                  <ScoreCard label={t('knowledge.storage.details.quality.relevance')} score={relevanceScore} t={t} />
                </div>
              )}

              {(totalChunks !== undefined || processedChunks !== undefined || chunkMethod || documentType || processingModel || processingProvider || processedAt) && (
                <div className="rounded-md border p-4 space-y-3">
                  <SectionLabel>{t('knowledge.storage.details.quality.processing')}</SectionLabel>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('knowledge.storage.details.quality.totalChunks')}</span>
                      <span className="font-medium tabular-nums">{totalChunks ?? '—'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('knowledge.storage.details.quality.processedChunks')}</span>
                      <span className="font-medium tabular-nums">{processedChunks ?? '—'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('knowledge.storage.details.quality.chunkMethod')}</span>
                      <span className="font-medium">
                        {chunkMethod ? (
                          <Badge variant="outline" className="font-normal text-[11px]">{chunkMethod}</Badge>
                        ) : '—'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('knowledge.storage.details.quality.documentType')}</span>
                      <span className="font-medium">
                        {documentType ? (
                          <Badge variant="outline" className="font-normal text-[11px]">{documentType}</Badge>
                        ) : '—'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('knowledge.storage.details.quality.model')}</span>
                      <span className="font-medium">
                        {processingModel ? (
                          <Badge variant="outline" className="font-normal text-[11px]">{processingModel}</Badge>
                        ) : '—'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('knowledge.storage.details.quality.provider')}</span>
                      <span className="font-medium">
                        {processingProvider ? (
                          <Badge variant="outline" className="font-normal text-[11px]">{processingProvider}</Badge>
                        ) : '—'}
                      </span>
                    </div>
                    {processedAt && (
                      <div className="flex justify-between col-span-2">
                        <span className="text-muted-foreground">{t('knowledge.storage.details.quality.processedAt')}</span>
                        <span className="font-medium tabular-nums">{formatDate(processedAt)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {hasDegradedSheets && (
                <div className="rounded-md border border-amber-300 bg-amber-50 dark:border-amber-700/60 dark:bg-amber-950/30 p-4">
                  <div className="flex gap-3">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                    <div className="space-y-2 text-sm">
                      <p className="font-medium text-amber-800 dark:text-amber-200">
                        {t('knowledge.storage.details.tabular.degraded.title')}
                      </p>
                      <p className="text-amber-700/90 dark:text-amber-200/80">
                        {t('knowledge.storage.details.tabular.degraded.description')}
                      </p>
                      <p className="text-amber-700/90 dark:text-amber-200/80">
                        <span className="font-medium">{t('knowledge.storage.details.tabular.degraded.ruleLabel')}</span>{' '}
                        {t('knowledge.storage.details.tabular.degraded.rule')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
        </TabsContent>

        {isSuperAdmin && (
          <TabsContent value="chunks" className={`${TAB_BODY_HEIGHT} mt-4`}>
            <div className="flex items-center justify-between mb-2 px-0.5">
              <SectionLabel>
                {chunksLoading && chunks.length === 0
                  ? t('common.loading')
                  : isTabular
                    ? `${t('knowledge.storage.details.tabular.sheets')} · ${chunksTotal}`
                    : t('knowledge.storage.details.chunks.total', { count: chunksTotal })}
              </SectionLabel>
            </div>

            {chunksError ? (
              <div className="text-sm text-destructive">{chunksError}</div>
            ) : chunksLoading && chunks.length === 0 ? (
              <div className="space-y-2">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : chunks.length === 0 ? (
              <EmptyState>{t('knowledge.storage.details.chunks.empty')}</EmptyState>
            ) : (
              <div className="flex flex-col gap-2">
                <ScrollArea className="h-[408px] w-full rounded-md border">
                  <div className={`p-2 space-y-2 ${chunksLoading ? 'opacity-60' : ''}`}>
                    {sortedChunks.map((chunk, idx) => (
                      <div
                        key={chunk.id}
                        className="border rounded-md p-3 bg-background hover:bg-muted/30 transition-colors"
                      >
                        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
                          <span className="font-medium truncate">
                            {isTabular
                              ? (chunk.metadata?.sheet_name as string | undefined)
                                ?? `#${(chunk.metadata?.sheet_index as number | undefined) ?? (chunksRangeStart - 1 + idx)}`
                              : `#${(chunk.metadata?.chunk_index as number | undefined) ?? (chunksRangeStart - 1 + idx)}`}
                          </span>
                          <span className="font-mono">{chunk.id.slice(0, 8)}…</span>
                        </div>
                        {isTabular && (
                          <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                            {typeof chunk.metadata?.representation === 'string' && (
                              <Badge variant="outline" className="font-normal text-[11px]">
                                {chunk.metadata.representation as string}
                              </Badge>
                            )}
                            {typeof chunk.metadata?.row_count === 'number' && (
                              <Badge variant="outline" className="font-normal text-[11px]">
                                {t('knowledge.storage.details.tabular.rowCount', { count: chunk.metadata.row_count as number })}
                              </Badge>
                            )}
                            {Array.isArray((chunk.metadata?.schema as { columns?: unknown[] } | undefined)?.columns) && (
                              <Badge variant="outline" className="font-normal text-[11px]">
                                {t('knowledge.storage.details.tabular.columnCount', {
                                  count: ((chunk.metadata!.schema as { columns: unknown[] }).columns).length,
                                })}
                              </Badge>
                            )}
                          </div>
                        )}
                        <pre className="text-xs whitespace-pre-wrap break-words leading-relaxed">
                          {chunk.content || ''}
                        </pre>
                      </div>
                    ))}
                  </div>
                </ScrollArea>

                {chunksTotalPages > 1 && (
                  <div className="flex items-center justify-end gap-1.5">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => setChunksPage((p) => Math.max(1, p - 1))}
                      disabled={chunksPage === 1 || chunksLoading}
                      aria-label="Previous page"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </Button>
                    <span className="text-xs text-muted-foreground tabular-nums px-1">
                      {t('common.pagination.pageOfTotal', {
                        current: chunksPage,
                        total: chunksTotalPages,
                      })}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => setChunksPage((p) => Math.min(chunksTotalPages, p + 1))}
                      disabled={chunksPage === chunksTotalPages || chunksLoading}
                      aria-label="Next page"
                    >
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | undefined | null }) {
  return (
    <div className="space-y-1">
      <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="text-sm">{value || '—'}</div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{children}</div>
  );
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}

/**
 * Detecta quando uma string contém um JSON serializado e devolve o valor
 * já parseado. Permite que o MetadataRow renderize-o como objeto/array
 * (bloco/badges) em vez de uma string crua. Retorna `undefined` quando
 * a string não parece JSON ou quando o parse falha.
 */
function maybeParseEmbeddedJson(s: string): unknown | undefined {
  if (typeof s !== 'string') return undefined;
  const trimmed = s.trim();
  if (trimmed.length < 2) return undefined;
  const first = trimmed[0];
  const last = trimmed[trimmed.length - 1];
  if (!((first === '{' && last === '}') || (first === '[' && last === ']'))) return undefined;
  try {
    return JSON.parse(trimmed);
  } catch {
    return undefined;
  }
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

/**
 * Heurística para escolher o campo "principal" de um objeto em metadata
 * (ex.: nome de um attendee). Preferimos campos legíveis em sequência;
 * caímos no primeiro valor-string como fallback.
 */
function pickPrimaryLabel(obj: Record<string, unknown>): { key: string; value: string } | null {
  const candidates = ['display_name', 'name', 'title', 'label', 'username', 'full_name', 'email', 'id'];
  for (const k of candidates) {
    const v = obj[k];
    if (typeof v === 'string' && v.trim()) return { key: k, value: v };
  }
  for (const [k, v] of Object.entries(obj)) {
    if (typeof v === 'string' && v.trim()) return { key: k, value: v };
  }
  return null;
}

function ObjectItemCard({ obj }: { obj: Record<string, unknown> }) {
  const primary = pickPrimaryLabel(obj);
  const rest = Object.entries(obj).filter(([k]) => k !== primary?.key);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border bg-background px-2.5 py-1.5">
      {primary && (
        <span className="text-xs font-medium leading-tight">{primary.value}</span>
      )}
      <div className="flex flex-wrap gap-1 ml-auto">
        {rest.map(([k, val]) => (
          <Badge key={k} variant="secondary" className="font-normal text-[10px] gap-1 py-0.5">
            <span className="text-muted-foreground/70">{k}</span>
            <span className="break-all">
              {typeof val === 'object' && val !== null ? JSON.stringify(val) : String(val)}
            </span>
          </Badge>
        ))}
      </div>
    </div>
  );
}

function MetadataRow({ k, value }: { k: string; value: unknown }) {
  // Se o valor é uma string que carrega JSON serializado (caso típico de
  // campos vindos de workflows que stringificam objetos antes de gravar
  // em metadata), trata como se fosse o objeto parseado. Mantém o
  // comportamento normal pra strings simples.
  const effective = typeof value === 'string'
    ? (maybeParseEmbeddedJson(value) ?? value)
    : value;
  const isPrimitive = effective === null || ['string', 'number', 'boolean'].includes(typeof effective);
  const isArray = Array.isArray(effective);
  const arr = isArray ? (effective as unknown[]) : null;
  const isArrayOfObjects = !!arr && arr.length > 0 && arr.every(isPlainObject);

  return (
    <div className="grid grid-cols-[180px_1fr] gap-3 px-3 py-2.5 text-sm hover:bg-muted/40 transition-colors">
      <div className="font-mono text-xs text-muted-foreground break-all pt-0.5">{k}</div>
      <div className="min-w-0">
        {isPrimitive ? (
          <span className="break-words">{effective === null ? '—' : String(effective)}</span>
        ) : isArrayOfObjects ? (
          <div className="space-y-1">
            {(arr as Record<string, unknown>[]).map((v, i) => (
              <ObjectItemCard key={i} obj={v} />
            ))}
          </div>
        ) : isArray ? (
          <div className="flex flex-wrap gap-1">
            {(arr as unknown[]).map((v, i) => (
              <Badge key={i} variant="outline" className="font-normal text-[11px]">
                {typeof v === 'object' ? JSON.stringify(v) : String(v)}
              </Badge>
            ))}
          </div>
        ) : (
          <pre className="text-xs whitespace-pre-wrap break-all bg-muted/40 rounded px-2 py-1 font-mono">
            {JSON.stringify(effective, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

function ScoreCard({
  label,
  score,
  t,
}: {
  label: string;
  score: number | undefined;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  if (score === undefined) {
    return (
      <div className="rounded-md border p-4 space-y-2">
        <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{label}</div>
        <div className="text-2xl font-semibold text-muted-foreground">—</div>
        <div className="text-xs text-muted-foreground">
          {t('knowledge.storage.details.quality.notAvailable')}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md border p-4 space-y-3">
      <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="flex items-baseline justify-between">
        <span className="text-2xl font-semibold tabular-nums">{score}</span>
        <span className="text-xs text-muted-foreground">/ 10</span>
      </div>
      <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={`h-full ${scoreTone(score)} transition-all`}
          style={{ width: `${Math.min(100, Math.max(0, score * 10))}%` }}
        />
      </div>
    </div>
  );
}
